/* CCPA / CPRA — "Do Not Sell or Share My Personal Information" + "Limit
   Use of My Sensitive Personal Information" rights page.

   verqo does not sell personal information in the colloquial sense, but
   we still surface this page + a way to opt out so California users
   (and any user invoking GPC) can exercise their statutory rights
   without friction. The form posts to /api/legal/privacy-request which
   records the request to the `privacy_requests` collection so a human
   compliance reviewer can process it within the 45-day statutory
   window. */
import { Link } from "react-router-dom";
import { Shield, ArrowLeft, Check } from "lucide-react";
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";

const REQUEST_TYPES = [
  { v: "do_not_sell",    l: "Do Not Sell or Share my personal info",   d: "CCPA / CPRA right under §1798.120" },
  { v: "limit_use",      l: "Limit the use of my sensitive personal info", d: "CCPA / CPRA right under §1798.121" },
  { v: "access",         l: "Right to know what info you have on me",  d: "CCPA §1798.100, §1798.110" },
  { v: "delete",         l: "Delete my personal info",                 d: "CCPA §1798.105" },
  { v: "correct",        l: "Correct inaccurate info",                 d: "CPRA §1798.106" },
  { v: "portability",    l: "Receive a portable copy",                 d: "CCPA §1798.100(d)" },
];

export default function DoNotSell() {
  const [form, setForm] = useState({ type: "do_not_sell", email: "", company: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  // If the browser sets Global Privacy Control, default to the "do_not_sell"
  // request and surface a small confirmation that the signal was received.
  useEffect(() => {
    if (typeof navigator !== "undefined" && navigator.globalPrivacyControl) {
      setForm(f => ({ ...f, type: "do_not_sell" }));
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return toast.error("Email is required so we can confirm your identity.");
    setBusy(true);
    try {
      const { data } = await api.post("/legal/privacy-request", form);
      setDone(data.id);
      toast.success("Request received. We will respond within 45 days.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="legal-do-not-sell">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="text-xs text-foreground/55 hover:text-foreground inline-flex items-center gap-1.5 mb-8">
          <ArrowLeft className="w-3 h-3"/>Back to verqo
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-foreground text-accent grid place-items-center">
            <Shield className="w-5 h-5"/>
          </div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">Legal · CCPA / CPRA rights</div>
        </div>
        <h1 className="display text-4xl tracking-tight mt-2">Do Not Sell or Share My Personal Information</h1>
        <div className="text-[12px] text-foreground/55 mt-2">v1.0 · Effective May 1, 2026</div>

        <div className="mt-8 space-y-4 text-foreground/85 leading-relaxed text-sm">
          <p>
            verqo is a B2B platform and does not "sell" personal information for monetary
            consideration. However, the California Consumer Privacy Act (CCPA), the California
            Privacy Rights Act (CPRA), and similar laws in other states give residents the right
            to opt out of any sharing of personal information for cross-context behavioral
            advertising, to limit our use of sensitive personal information, and to request
            access, deletion, correction, or portability of their data.
          </p>
          <p>
            Use the form below to submit any of these requests. We respond within <strong>45
            days</strong> (subject to a 45-day extension we will notify you of if needed). We
            verify your identity by emailing the address you provide.
          </p>
        </div>

        {done ? (
          <div className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 flex items-start gap-3" data-testid="dns-confirmation">
            <div className="w-9 h-9 rounded-full bg-emerald-600 text-white grid place-items-center shrink-0">
              <Check className="w-4 h-4"/>
            </div>
            <div>
              <div className="font-semibold text-emerald-900">Request received.</div>
              <div className="text-sm text-emerald-900/85 mt-1">
                Reference <code className="font-mono">{done}</code>. We will email you within
                45 days from the address you provided.
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 rounded-2xl border border-border bg-white p-6 space-y-4" data-testid="dns-form">
            <div>
              <div className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/55 mb-2">Request type</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {REQUEST_TYPES.map(t => (
                  <label key={t.v}
                    className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer transition-colors ${form.type === t.v ? "border-foreground bg-foreground/[0.03]" : "border-foreground/15 hover:border-foreground/40"}`}
                    data-testid={`dns-type-${t.v}`}
                  >
                    <input type="radio" name="type" checked={form.type === t.v} onChange={() => setForm({ ...form, type: t.v })} className="mt-1"/>
                    <span>
                      <span className="text-[13px] font-medium block">{t.l}</span>
                      <span className="text-[11px] text-foreground/55">{t.d}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/55">Email (required to verify identity)</label>
              <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                data-testid="dns-email" className="mt-1 w-full h-11 px-3 rounded-md border border-foreground/15 bg-white text-sm"/>
            </div>
            <div>
              <label className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/55">Company (optional)</label>
              <input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })}
                data-testid="dns-company" className="mt-1 w-full h-11 px-3 rounded-md border border-foreground/15 bg-white text-sm"/>
            </div>
            <div>
              <label className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/55">Additional context (optional)</label>
              <textarea rows={3} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}
                data-testid="dns-note" className="mt-1 w-full px-3 py-2 rounded-md border border-foreground/15 bg-white text-sm"/>
            </div>
            <button type="submit" disabled={busy}
              data-testid="dns-submit"
              className="w-full sm:w-auto h-11 px-5 rounded-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50">
              {busy ? "Sending…" : "Submit request"}
            </button>
            <p className="text-[11px] text-foreground/55">
              We will not retaliate against you for exercising these rights. Service quality
              will not be diminished. We do not require account creation to submit a request.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
